/**
 * @file Google Places API Service
 * @description Centralized service for all Google Places API interactions
 * 
 * FIELD CONFIGURATION:
 * This file defines standard field sets for different use cases:
 * - MINIMAL: Quick lookups ($0.017)
 * - SEARCH: Search result caching ($0.017)
 * - ESSENTIAL: User-facing details with contact info ($0.020)
 * - ENHANCED: Rich profiles with hours ($0.025)
 * - MAXIMUM: Complete data for backfill ($0.032)
 * 
 * USAGE:
 * ```javascript
 * // Recommended: Use predefined field sets
 * const details = await fetchPlaceDetails(placeId, { fieldSet: 'maximum' });
 * 
 * // Custom: Specify exact fields
 * const details = await fetchPlaceDetails(placeId, { 
 *   fields: ['places.id', 'places.displayName', 'places.websiteUri'] 
 * });
 * ```
 * 
 * COST TRACKING:
 * The service logs estimated costs in development mode.
 * Use estimateFieldCost() to calculate costs before making requests.
 * 
 * API FORMAT:
 * This service uses Google Places API v1 (places.googleapis.com/v1)
 * Field masks use format: 'places.id', 'places.displayName', etc.
 */

import { searchCampsInDatabase, batchSaveCamps } from './campDatabaseService.js';
import { deduplicateRequest } from '@/utils/requestDeduplication';
// Lazy import for enhancedCampDatabaseService (has path aliases that don't work in Node.js)
let batchSaveGooglePlaces;
async function getBatchSaveGooglePlaces() {
  if (!batchSaveGooglePlaces) {
    try {
      const module = await import('./enhancedCampDatabaseService.js');
      batchSaveGooglePlaces = module.batchSaveGooglePlaces;
    } catch (error) {
      // In Node.js, this function isn't needed for backfill scripts
      batchSaveGooglePlaces = null;
    }
  }
  return batchSaveGooglePlaces;
}

// Support both Vite (browser) and Node.js environments
// In Node.js, import.meta exists but import.meta.env doesn't (that's Vite-specific)
// Use a function to get API key lazily to ensure env vars are loaded
function getApiKey() {
  // In Node.js (server-side), prioritize GOOGLE_MAPS_API_KEY (set up for server-side workflow)
  const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;
  const isVite = typeof import.meta !== 'undefined' && import.meta.env;
  
  if (isNode && !isVite) {
    // Node.js environment - prioritize GOOGLE_MAPS_API_KEY (server-side key without referrer restrictions)
    return process.env.GOOGLE_MAPS_API_KEY 
      || process.env.GOOGLE_PLACES_API_KEY 
      || process.env.VITE_GOOGLE_MAPS_API_KEY  // Fallback to browser key
      || null;
  }
  
  // Browser/Vite environment - use Vite env vars
  if (isVite && import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  }
  
  // Fallback for any other case
  return process.env.GOOGLE_MAPS_API_KEY 
    || process.env.GOOGLE_PLACES_API_KEY 
    || process.env.VITE_GOOGLE_MAPS_API_KEY 
    || null;
}

const BASE_URL = 'https://places.googleapis.com/v1/places';

/**
 * Google Places API Field Configurations
 * 
 * Google Places API charges based on field tiers:
 * - Basic Data: $0.017 per request
 * - Contact Data: +$0.003 per request (additional)
 * - Atmosphere Data: +$0.005 per request (additional)
 * 
 * Total cost for all tiers: $0.017 + $0.003 + $0.005 = $0.025
 * (Note: Some sources show $0.032 for full details, verify current pricing)
 * 
 * Reference: https://developers.google.com/maps/documentation/places/web-service/usage-and-billing
 * 
 * NOTE: This service uses Places API v1 format with field masks like 'places.id', 'places.displayName'
 * For the old REST API format, field names would be 'place_id', 'name', etc.
 */
export const GOOGLE_PLACES_FIELD_TIERS = {
  /**
   * Basic Data - $0.017 per request
   * Includes fundamental place information
   */
  BASIC: [
    'places.id',                      // Unique identifier
    'places.displayName',             // Place name
    'places.types',                   // Google place types array
    'places.formattedAddress',        // Full formatted address
    'places.location',                // Location coordinates (lat/lng)
    'places.businessStatus',          // OPERATIONAL, CLOSED_TEMPORARILY, etc.
    // Note: Some fields may not be available in GET endpoint - commented out for now
    // 'places.icon',                    // Google's icon URL
    // 'places.iconBackgroundColor',     // Icon background color
    // 'places.iconMaskBaseUri',         // Icon mask URI
    // 'places.plusCode',                // Plus codes for location
    // 'places.googleMapsUri',           // Google Maps URL for this place
    // 'places.shortFormattedAddress',    // Simplified address (more readable)
    // 'places.utcOffset',               // UTC offset in minutes
    // 'places.wheelchairAccessibleEntrance', // Accessibility boolean
    // 'places.editorialSummary'          // Google's editorial description
  ],

  /**
   * Contact Data - +$0.003 per request (additional)
   * Contact information for the place
   */
  CONTACT: [
    'places.nationalPhoneNumber',     // US formatted phone (e.g., "(555) 123-4567")
    'places.internationalPhoneNumber', // E.164 format (e.g., "+1 555-123-4567")
    'places.websiteUri',              // Official website URL
    'places.addressComponents'        // Structured address parts
  ],

  /**
   * Atmosphere Data - +$0.005 per request (additional)
   * User-generated and experiential data
   */
  ATMOSPHERE: [
    'places.rating',                  // Average rating (0.0-5.0)
    'places.userRatingCount',         // Number of ratings
    'places.reviews',                 // Array of review objects
    'places.photos',                  // Array of photo references
    'places.priceLevel',              // Price level (0-4 scale)
    'places.regularOpeningHours',      // Standard opening hours
    'places.currentOpeningHours'      // Current opening hours (includes seasonal)
  ]
};

/**
 * Predefined field sets for different use cases
 * Use these instead of manually specifying fields
 */
export const FIELD_SETS = {
  /**
   * MINIMAL - For quick lookups and existence checks
   * Cost: $0.017 per request
   */
  MINIMAL: [
    'places.id',
    'places.displayName',
    'places.location',
    'places.businessStatus'
  ],

  /**
   * SEARCH - For caching search results (cost-optimized)
   * Only essential data for displaying in search results
   * Cost: $0.017 per request
   */
  SEARCH: [
    'places.id',
    'places.displayName',
    'places.types',
    'places.formattedAddress',
    'places.location',
    'places.businessStatus',
    'places.rating',
    'places.userRatingCount',
    'places.priceLevel',
    'places.editorialSummary'
  ],

  /**
   * ESSENTIAL - For user-facing camp details
   * Includes contact info that users need
   * Cost: $0.020 per request (Basic + Contact)
   */
  ESSENTIAL: [
    ...GOOGLE_PLACES_FIELD_TIERS.BASIC,
    ...GOOGLE_PLACES_FIELD_TIERS.CONTACT
  ],

  /**
   * ENHANCED - For rich camp profiles
   * Includes operating hours and accessibility
   * Cost: $0.025 per request (Basic + Contact + Atmosphere subset)
   */
  ENHANCED: [
    ...GOOGLE_PLACES_FIELD_TIERS.BASIC,
    ...GOOGLE_PLACES_FIELD_TIERS.CONTACT,
    'places.regularOpeningHours',
    'places.currentOpeningHours',
    'places.rating',
    'places.userRatingCount'
  ],

  /**
   * MAXIMUM - All available useful fields
   * Complete camp information for backfill and detailed views
   * Cost: $0.032 per request (All tiers)
   */
  MAXIMUM: [
    ...GOOGLE_PLACES_FIELD_TIERS.BASIC,
    ...GOOGLE_PLACES_FIELD_TIERS.CONTACT,
    ...GOOGLE_PLACES_FIELD_TIERS.ATMOSPHERE
  ]
};

/**
 * Format field array into Google Places API field mask
 * Google API expects comma-separated field names
 * 
 * @param {string[]} fieldSet - Array of field names
 * @returns {string} Comma-separated field mask
 * 
 * @example
 * formatFieldMask(['places.id', 'places.displayName', 'places.rating'])
 * // Returns: "places.id,places.displayName,places.rating"
 */
export function formatFieldMask(fieldSet) {
  return fieldSet.join(',');
}

/**
 * Calculate approximate cost for a field set
 * 
 * @param {string[]} fieldSet - Array of field names
 * @returns {number} Estimated cost in USD
 */
export function estimateFieldCost(fieldSet) {
  let cost = 0;

  const hasBasicFields = fieldSet.some(f => GOOGLE_PLACES_FIELD_TIERS.BASIC.includes(f));
  const hasContactFields = fieldSet.some(f => GOOGLE_PLACES_FIELD_TIERS.CONTACT.includes(f));
  const hasAtmosphereFields = fieldSet.some(f => GOOGLE_PLACES_FIELD_TIERS.ATMOSPHERE.includes(f));

  if (hasBasicFields) cost += 0.017;
  if (hasContactFields) cost += 0.003;
  if (hasAtmosphereFields) cost += 0.005;

  return cost;
}

/**
 * Get recommended field set based on use case
 * 
 * @param {string} useCase - 'search' | 'view' | 'backfill' | 'maximum' | 'minimal' | 'enhanced'
 * @returns {string[]} Appropriate field set
 */
export function getFieldSetForUseCase(useCase) {
  switch (useCase) {
    case 'minimal':
      return FIELD_SETS.MINIMAL;
    case 'search':
      return FIELD_SETS.SEARCH;
    case 'view':
      return FIELD_SETS.ESSENTIAL;
    case 'backfill':
    case 'maximum':  // Alias for backfill - fetches all Maximum tier fields
      return FIELD_SETS.MAXIMUM;
    case 'enhanced':
      return FIELD_SETS.ENHANCED;
    default:
      console.warn(`Unknown use case: ${useCase}, defaulting to SEARCH`);
      return FIELD_SETS.SEARCH;
  }
}

// Basic Data tier field mask - Cost-optimized for enhanced discovery
// Now uses centralized FIELD_SETS.SEARCH for consistency
// Note: Includes photos and reviews which are part of Basic tier in Places API v1
const BASIC_FIELD_MASK = formatFieldMask([
  ...FIELD_SETS.SEARCH,
  'places.photos',              // ✅ Photos (Basic tier)
  'places.reviews'              // ✅ Reviews (Basic tier)
]);

/**
 * Validates if a string is a valid Google Place ID
 * Google Place IDs typically start with ChIJ/GhIJ/EhIJ and are not UUIDs
 */
const isValidGooglePlaceId = (placeId) => {
  if (!placeId || typeof placeId !== 'string') return false;
  const validPrefixes = ['ChIJ', 'GhIJ', 'EhIJ'];
  const hasValidPrefix = validPrefixes.some((prefix) => placeId.startsWith(prefix));
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(placeId);
  return hasValidPrefix && !isUUID;
};

// Cost tracking constants
const COST_TRACKING = {
  BASIC_DATA_COST: 0.017,        // $0.017 per request
  ADVANCED_DATA_COST: 0.05,      // $0.05 per request (avoided!)
  PHOTO_SKU_COST: 0.007,         // $0.007 per photo (included in Basic!)
  REVIEW_SKU_COST: 0.005         // $0.005 per review (included in Basic!)
};

// API usage counter (in-memory, resets on page reload)
let apiCallCounter = {
  searchNearby: 0,
  textSearch: 0,
  textSearchByQuery: 0,
  searchAllYouthPrograms: 0,
  totalCalls: 0,
  totalEstimatedCost: 0
};

/**
 * Log API call for cost monitoring
 */
const logAPICall = (endpoint, details = {}) => {
  apiCallCounter[endpoint] = (apiCallCounter[endpoint] || 0) + 1;
  apiCallCounter.totalCalls += 1;
  apiCallCounter.totalEstimatedCost += COST_TRACKING.BASIC_DATA_COST;
  
  const costInfo = {
    endpoint,
    timestamp: new Date().toISOString(),
    callNumber: apiCallCounter[endpoint],
    totalCalls: apiCallCounter.totalCalls,
    fieldMask: 'BASIC_DATA_TIER',
    fieldsRequested: BASIC_FIELD_MASK.split(',').length,
    estimatedCost: `$${COST_TRACKING.BASIC_DATA_COST.toFixed(3)}`,
    sessionTotal: `$${apiCallCounter.totalEstimatedCost.toFixed(3)}`,
    costSavings: `$${(COST_TRACKING.ADVANCED_DATA_COST - COST_TRACKING.BASIC_DATA_COST).toFixed(3)} saved vs Advanced tier`,
    ...details
  };
  
  console.log('📊 ==========================================');
  console.log('📊 GOOGLE PLACES API CALL');
  console.log('📊 ==========================================');
  console.log('📊 Endpoint:', costInfo.endpoint);
  console.log('📊 Timestamp:', costInfo.timestamp);
  console.log('📊 Call #', costInfo.callNumber, 'of this type');
  console.log('📊 Total calls this session:', costInfo.totalCalls);
  console.log('📊 Field Mask:', costInfo.fieldMask);
  console.log('📊 Fields Requested:', costInfo.fieldsRequested);
  console.log('💰 Estimated Cost:', costInfo.estimatedCost);
  console.log('💰 Session Total Cost:', costInfo.sessionTotal);
  console.log('💰 Cost Savings:', costInfo.costSavings);
  if (details.location) console.log('📍 Location:', details.location);
  if (details.query) console.log('🔍 Query:', details.query);
  if (details.radius) console.log('📏 Radius:', details.radius);
  console.log('📊 ==========================================');
  
  return costInfo;
};

/**
 * Get current API usage statistics
 */
export const getAPIUsageStats = () => {
  return {
    ...apiCallCounter,
    costPerCall: COST_TRACKING.BASIC_DATA_COST,
    savingsPerCall: COST_TRACKING.ADVANCED_DATA_COST - COST_TRACKING.BASIC_DATA_COST,
    totalSavings: apiCallCounter.totalCalls * (COST_TRACKING.ADVANCED_DATA_COST - COST_TRACKING.BASIC_DATA_COST)
  };
};

// Youth program relevant place types - COMPREHENSIVE LIST
// Cast a WIDE net in API search, then filter in UI
const YOUTH_PROGRAM_TYPES = [
  // Educational
  'school',
  'university',
  'primary_school',
  'secondary_school',
  'preschool',
  
  // Sports & Recreation
  'gym',
  'sports_club',
  'sports_complex',
  'stadium',
  'swimming_pool',
  
  // Outdoor & Adventure
  'campground',
  'park',
  'national_park',
  'rv_park',
  
  // Arts & Culture
  'museum',
  'art_gallery',
  'performing_arts_theater',
  'library',
  
  // STEM & Learning
  'aquarium',
  'zoo',
  'planetarium',
  
  // Community
  'community_center',
  'child_care_agency',
  
  // Entertainment
  'amusement_park',
  'bowling_alley',
  'movie_theater'
];

/**
 * Search for places near a location
 */
export const searchNearbyPlaces = async (location, radiusMiles = 5) => {
  try {
    // Validate input
    if (!location || !location.lat || !location.lng) {
      console.error('❌ Invalid location provided:', location);
      throw new Error('Invalid location data');
    }

    const radiusMeters = radiusMiles * 1609.34;
    
    // 📊 Cost Monitoring: Log API call
    logAPICall('searchNearby', {
      location: `${location.lat}, ${location.lng}`,
      radius: `${radiusMiles} miles (${radiusMeters.toFixed(0)}m)`,
      types: YOUTH_PROGRAM_TYPES.length
    });
    
    console.log('🔍 Searching near:', location, 'radius:', radiusMiles, 'miles');
    console.log('📍 Location data type check:', {
      lat: typeof location.lat,
      lng: typeof location.lng,
      latValue: location.lat,
      lngValue: location.lng
    });
    console.log('🔍 Searching with ALL youth program types:', YOUTH_PROGRAM_TYPES.length, 'types');

    // CRITICAL: Request body must match Google's schema exactly
    const requestBody = {
      includedTypes: YOUTH_PROGRAM_TYPES,
      maxResultCount: 20,
      locationRestriction: {
        circle: {
          center: {
            latitude: Number(location.lat),   // Must be numbers
            longitude: Number(location.lng)   // Must be numbers
          },
          radius: Number(radiusMeters)        // Must be a number
        }
      },
      rankPreference: 'DISTANCE'
    };

    console.log('📤 API Request:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${BASE_URL}:searchNearby`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': getApiKey(),
        'X-Goog-FieldMask': BASIC_FIELD_MASK
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ API Response received:', data.places?.length || 0, 'places');
    
    return transformPlaces(data.places || []);
  } catch (error) {
    console.error('❌ Nearby search failed:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Text-based search for places
 */
export const textSearchPlaces = async (query, location = null) => {
  try {
    console.log('🔍 textSearchPlaces called with:', { query, location });
    
    // Handle "near me" queries - replace with location-specific query if location is available
    let processedQuery = query;
    if (location && (query.toLowerCase().includes('near me') || query.toLowerCase().includes('nearby'))) {
      // For "near me" queries, we'll use location bias instead of modifying the query
      // This allows Google Places to find relevant places near the location
      processedQuery = query.replace(/near me|nearby/gi, '').trim();
      // If query becomes empty after removing "near me", add a generic search term
      if (!processedQuery) {
        processedQuery = 'summer programs';
      }
    }
    
    const requestBody = {
      textQuery: processedQuery,
      maxResultCount: 20
    };

    // Add location bias if provided (especially important for "near me" queries)
    if (location) {
      // Use larger radius for "near me" queries (50km = ~31 miles)
      const radiusMeters = query.toLowerCase().includes('near me') || query.toLowerCase().includes('nearby')
        ? 50000  // 50km for "near me" queries
        : 50000; // Default 50km
      
      requestBody.locationBias = {
        circle: {
          center: {
            latitude: location.lat,
            longitude: location.lng
          },
          radius: radiusMeters
        }
      };
    }

    console.log('📤 Text search request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${BASE_URL}:searchText`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': getApiKey(),
        'X-Goog-FieldMask': BASIC_FIELD_MASK
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Text search API Error Response:', errorText);
      console.error('❌ Response status:', response.status);
      console.error('❌ Response headers:', Object.fromEntries(response.headers.entries()));
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Text search API Response received:', data.places?.length || 0, 'places');
    console.log('📊 Text search response data:', data);
    
    return transformPlaces(data.places || []);
  } catch (error) {
    console.error('Text search failed:', error);
    return [];
  }
};

/**
 * Text search specifically for camps by keywords
 */
export const textSearchCamps = async (location, radiusMiles = 5) => {
  try {
    const radiusMeters = radiusMiles * 1609.34;
    
    // Search specifically for camp-related keywords
    const campKeywords = [
      'summer camp',
      'day camp', 
      'youth camp',
      'kids camp',
      'children camp',
      'YMCA camp',
      'scout camp'
    ];
    
    // 📊 Cost Monitoring: Log API call
    logAPICall('textSearch', {
      location: location ? `${location.lat}, ${location.lng}` : 'N/A',
      radius: `${radiusMiles} miles (${radiusMeters.toFixed(0)}m)`,
      keywords: campKeywords.length
    });
    
    console.log('🏕️ Text searching for camps with keywords');
    
    // Search for each keyword
    const searchPromises = campKeywords.map(keyword => {
      const requestBody = {
        textQuery: `${keyword} near me`,
        maxResultCount: 10,
        locationBias: {
          circle: {
            center: {
              latitude: Number(location.lat),
              longitude: Number(location.lng)
            },
            radius: Number(radiusMeters)
          }
        }
      };
      
      return fetch(`${BASE_URL}:searchText`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': getApiKey(),
          'X-Goog-FieldMask': BASIC_FIELD_MASK
        },
        body: JSON.stringify(requestBody)
      }).then(res => res.ok ? res.json() : { places: [] });
    });
    
    // Wait for all searches to complete
    const results = await Promise.all(searchPromises);
    
    // Combine and deduplicate results
    const allPlaces = results.flatMap(result => result.places || []);
    const uniquePlaces = deduplicatePlaces(allPlaces);
    
    console.log(`✅ Text search found ${uniquePlaces.length} camps`);
    
    return transformPlaces(uniquePlaces);
  } catch (error) {
    console.error('❌ Text search failed:', error);
    return [];
  }
};

/**
 * Deduplicate places by ID
 */
const deduplicatePlaces = (places) => {
  const seen = new Set();
  return places.filter(place => {
    if (!place?.id) return false;
    if (seen.has(place.id)) return false;
    seen.add(place.id);
    return true;
  });
};

/**
 * Search by text query - Google Places API call
 * NOTE: This function directly calls Google API without database check.
 * For cost-optimized search with database-first strategy, use optimizedSearch.textLocationSearch()
 * 
 * Perfect for finding specific camps like "High Meadows Day Camp"
 * 
 * @param {string} query - Search query
 * @param {number} radiusMiles - Search radius in miles
 * @param {object} userLocation - Optional location bias {lat, lng}
 * @returns {array} Array of camp results
 */
export const textSearchByQuery = async (query, radiusMiles = 10, userLocation = null) => {
  try {
    console.log('🔍 Google Text Search API for query:', query);
    
    // Use deduplication to prevent duplicate searches
    const cacheKey = `textSearch:${query}:${radiusMiles}:${userLocation?.lat}:${userLocation?.lng}`;
    
    return await deduplicateRequest(cacheKey, async () => {
      // 📊 Cost Monitoring: Log API call
      logAPICall('textSearchByQuery', {
        query,
        location: userLocation ? `${userLocation.lat}, ${userLocation.lng}` : 'No location bias',
        radius: `${radiusMiles} miles`
      });
      
      // Build search query variations - optimized to reduce API calls
      // Only use variations if original query doesn't contain camp-related terms
      const hasCampTerm = /camp|program|youth|summer|day|overnight|sleepaway/i.test(query);
      
      const searchQueries = hasCampTerm 
        ? [
            query, // Original query
            `${query} near me` // Add location context if it's already camp-related
          ]
        : [
            query, // Original query
            `${query} summer camp` // Add summer camp term if not present
          ];
      
      console.log('🔍 Searching with', searchQueries.length, 'query variations (optimized)');
      
      // Search with each query variation
      const searchPromises = searchQueries.map(async (searchQuery) => {
      const requestBody = {
        textQuery: searchQuery,
        maxResultCount: 20
      };
      
      // If we have user location, bias results towards it
      if (userLocation && userLocation.lat && userLocation.lng) {
        requestBody.locationBias = {
          circle: {
            center: {
              latitude: Number(userLocation.lat),
              longitude: Number(userLocation.lng)
            },
            radius: radiusMiles * 1609.34 // Convert miles to meters
          }
        };
      }
      
      const response = await fetch(`${BASE_URL}:searchText`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': getApiKey(),
          'X-Goog-FieldMask': BASIC_FIELD_MASK
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        console.warn(`Search failed for "${searchQuery}"`);
        return { places: [] };
      }
      
      return await response.json();
      });
      
      // Wait for all searches
      const results = await Promise.all(searchPromises);
      
      // Combine and deduplicate
      const allPlaces = results.flatMap(r => r.places || []);
      const uniquePlaces = deduplicatePlaces(allPlaces);
      
      console.log(`✅ Text search found ${uniquePlaces.length} places for "${query}"`);
      
      // Transform results
      const transformedResults = transformPlaces(uniquePlaces);
      
      // Save results to database for caching (same as searchAllYouthPrograms)
      if (transformedResults.length > 0) {
        console.log(`💾 Attempting to save ${transformedResults.length} text search results to database...`);
        try {
          const saveFunction = await getBatchSaveGooglePlaces();
          if (saveFunction) {
            const saveResult = await saveFunction(transformedResults);
            console.log(`✅ Text search database save complete:`, saveResult.summary);
            if (saveResult.errors.length > 0) {
              console.warn(`⚠️ ${saveResult.errors.length} errors during text search save:`, saveResult.errors);
            }
          }
        } catch (err) {
          console.error('❌ Failed to cache text search results:', err);
        }
      }
      
      return transformedResults;
    }, { useCache: true, cacheTTL: 300000 }); // 5 minute cache for search results
    
  } catch (error) {
    console.error('❌ Text search failed:', error);
    return [];
  }
};

/**
 * Score and sort results to prioritize camps
 */
export const prioritizeCamps = (programs) => {
  console.log('🎯 Prioritizing camps in results');
  
  return programs
    .map(program => {
      let priorityScore = 0;
      
      const name = (program.name || '').toLowerCase();
      const types = program.types || [];
      
      // High priority: Contains "camp" in name
      if (name.includes('camp')) priorityScore += 100;
      if (name.includes('summer camp')) priorityScore += 50;
      if (name.includes('day camp')) priorityScore += 50;
      if (name.includes('ymca')) priorityScore += 30;
      if (name.includes('scout')) priorityScore += 30;
      
      // Medium priority: Relevant types
      if (types.includes('campground')) priorityScore += 40;
      if (types.includes('community_center')) priorityScore += 20;
      if (types.includes('park')) priorityScore += 10;
      
      // Bonus for good ratings
      if (program.rating) {
        priorityScore += program.rating * 5;
      }
      
      return { ...program, priorityScore };
    })
    .sort((a, b) => b.priorityScore - a.priorityScore);
};

/**
 * ENHANCED: Search with database cache layer
 * Checks database cache first, falls back to Google Places API
 */
export const searchAllYouthPrograms = async (location, radiusMiles = 5, options = {}) => {
  const { forceRefresh = false, categories = [] } = options;
  
  console.log('🔍 Starting comprehensive youth program search');
  console.log('📍 Location:', location, 'Radius:', radiusMiles, 'miles');
  
  // Step 1: Check database cache first (unless force refresh)
  if (!forceRefresh) {
    const cachedResults = await searchCampsInDatabase({
      location,
      radius: radiusMiles,
      categories
    });
    
    // Reduced threshold from 10 to 5 - use database if we have 5+ results
    if (cachedResults.length >= 5) {
      console.log(`✅ Using ${cachedResults.length} cached results from database`);
      
      // Transform database format to app format
      const transformedResults = cachedResults.map(transformDatabaseCamp);
      
      // Prioritize camps to show them first
      const prioritized = prioritizeCamps(transformedResults);
      
      console.log('🎯 Cached results prioritized, top 5:');
      prioritized.slice(0, 5).forEach((p, i) => {
        console.log(`   ${i + 1}. ${p.name} (score: ${p.priorityScore})`);
      });
      
      // Optionally: Still fetch from API in background to update cache
      backgroundRefreshCamps(location, radiusMiles);
      
      return prioritized;
    } else {
      console.log(`⚠️ Only ${cachedResults.length} cached results, fetching from Google API...`);
    }
  }
  
  // Step 2: Fetch from Google Places API (original logic)
  console.log('📡 Fetching from Google Places API');
  
  // 📊 Cost Monitoring: Log API call (this is a composite call)
  logAPICall('searchAllYouthPrograms', {
    location: `${location.lat}, ${location.lng}`,
    radius: `${radiusMiles} miles`,
    note: 'Composite search (nearby + text), costs already logged separately'
  });
  
  try {
    // Run both searches in parallel
    const [nearbyResults, textResults] = await Promise.all([
      searchNearbyPlaces(location, radiusMiles),
      textSearchCamps(location, radiusMiles)
    ]);
    
    console.log('📊 Search results breakdown:');
    console.log('   - Nearby search (by type):', nearbyResults.length);
    console.log('   - Text search (by keyword):', textResults.length);
    
    // Combine results
    const allResults = [...nearbyResults, ...textResults];
    
    // Deduplicate by place_id
    const uniqueResults = Array.from(
      new Map(allResults.map(p => [p.place_id, p])).values()
    );
    
    console.log(`✅ Total unique programs found: ${uniqueResults.length}`);
    
            // Step 3: Save results to database for future caching (CRITICAL - ensures all Google results are saved)
            if (uniqueResults.length > 0) {
              console.log(`💾 Attempting to save ${uniqueResults.length} results to database...`);
              // Use enhanced database service for better error handling
              // IMPORTANT: This ensures all Google API results are persisted for future searches
              try {
                const saveFunction = await getBatchSaveGooglePlaces();
                if (saveFunction) {
                  const saveResult = await saveFunction(uniqueResults);
                  if (saveResult) {
                    console.log(`✅ Database save complete:`, saveResult.summary);
                    if (saveResult.errors && saveResult.errors.length > 0) {
                      console.warn(`⚠️ ${saveResult.errors.length} errors during save:`, saveResult.errors);
                    }
                  }
                }
              } catch (err) {
                console.error('❌ Failed to cache results:', err);
                console.error('Error details:', err.message, err.stack);
                // Don't throw - caching failure shouldn't break search, but log it
              }
            } else {
              console.log('⏭️ No results to save to database');
            }
    
    // Prioritize camps to show them first
    const prioritized = prioritizeCamps(uniqueResults);
    
    console.log('🎯 Results prioritized, top 5:');
    prioritized.slice(0, 5).forEach((p, i) => {
      console.log(`   ${i + 1}. ${p.name} (score: ${p.priorityScore})`);
    });
    
    return prioritized;
  } catch (error) {
    console.error('❌ Comprehensive search failed:', error);
    return [];
  }
};

/**
 * Transform new API format to app format with cost-optimized photo strategy
 */
const transformPlaces = (places) => {
  if (!Array.isArray(places)) {
    console.error('❌ transformPlaces received non-array:', places);
    return [];
  }

  return places
    .map((place, index) => {
      try {
        if (!place) {
          console.warn(`⚠️ Place at index ${index} is null`);
          return null;
        }

        // Validate location exists
        if (!place.location?.latitude || !place.location?.longitude) {
          console.warn(`⚠️ Place "${place.displayName?.text}" missing location`);
          return null;
        }

        const transformed = {
          place_id: place.id,
          name: place.displayName?.text || 'Unknown',
          formatted_address: place.formattedAddress || '',
          
          // Location data
          location: {
            lat: Number(place.location.latitude),
            lng: Number(place.location.longitude)
          },
          geometry: {
            location: {
              lat: () => Number(place.location.latitude),
              lng: () => Number(place.location.longitude)
            }
          },
          
          // Basic ratings
          rating: place.rating || null,
          user_ratings_total: place.userRatingCount || 0,
          reviewCount: place.userRatingCount || 0, // Add reviewCount alias for component compatibility
          types: place.types || [],
          business_status: place.businessStatus || 'OPERATIONAL',
          
          // ✅ COST-OPTIMIZED: Single hero photo only
          photos: place.photos && place.photos.length > 0 ? (() => {
            const firstPhoto = place.photos[0];
            
            // Extract photo reference from the name (format: "places/ChIJ.../photos/Aap_uEA7vS8tMkxW_5U7wWp2Jg")
            const photoReference = firstPhoto.name.split('/').pop();
            
            // Construct photo URL using the NEW Places API (v1) format
            // The new format uses the photo name directly
            const photoKey = typeof import.meta !== 'undefined' && import.meta.env
              ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY
              : getApiKey();
            const photoUrl = `https://places.googleapis.com/v1/${firstPhoto.name}/media?maxWidthPx=400&key=${photoKey}`;
            
            console.log('📸 Photo processing for:', place.displayName?.text);
            console.log('📸 Raw photos array:', place.photos);
            console.log('📸 First photo:', firstPhoto);
            console.log('📸 Photo reference extracted:', photoReference);
            console.log('📸 Constructed photoUrl:', photoUrl);
            
            return [{
              name: firstPhoto.name,
              photoReference: photoReference,
              widthPx: firstPhoto.widthPx,
              heightPx: firstPhoto.heightPx,
              photoUrl: photoUrl
            }];
          })() : (() => {
            console.log('📸 No photos found for:', place.displayName?.text);
            console.log('📸 Photos array:', place.photos);
            return [];
          })(),
          
          // ✅ Add image_url for compatibility with existing components
          image_url: place.photos && place.photos.length > 0 ? (() => {
            const firstPhoto = place.photos[0];
            
            // Validate that photo.name exists and is a valid string
            if (!firstPhoto || !firstPhoto.name || typeof firstPhoto.name !== 'string') {
              console.warn('⚠️ placesAPI: Invalid photo object or missing photo.name for place:', {
                placeId: place.id,
                placeName: place.displayName?.text,
                photoObject: firstPhoto
              });
              return null;
            }
            
            // Validate photo.name format - must start with "places/"
            if (!firstPhoto.name.startsWith('places/')) {
              console.warn('⚠️ placesAPI: Invalid photo.name format (expected "places/..."):', {
                placeId: place.id,
                placeName: place.displayName?.text,
                photoName: firstPhoto.name
              });
              return null;
            }
            
            // Validate API key
            const apiKey = typeof import.meta !== 'undefined' && import.meta.env
              ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY
              : getApiKey();
            if (!apiKey || apiKey.trim() === '') {
              console.warn('⚠️ placesAPI: Missing or empty Google Maps API key');
              return null;
            }
            
            const constructedUrl = `https://places.googleapis.com/v1/${firstPhoto.name}/media?maxWidthPx=400&key=${apiKey}`;
            
            console.log('🔍 placesAPI Photo Debug:', {
              placeId: place.id,
              placeName: place.displayName?.text,
              photoName: firstPhoto.name,
              photoNameFormat: 'valid',
              apiKeyExists: true,
              constructedUrl: constructedUrl
            });
            
            // Ensure image_url is never a Place ID - final validation
            if (/^(ChIJ|GhIJ|EhIJ)/.test(constructedUrl)) {
              console.error('❌ placesAPI: Constructed URL appears to be a Place ID! This should never happen:', constructedUrl);
              return null;
            }
            
            return constructedUrl;
          })() : null,
          
          // Store photo count for future "load more" feature
          photo_count: place.photos?.length || 0,
          
          // ✅ Reviews (limit to top 3 for cost optimization) - use saved format for consistency
          reviews: (place.reviews || []).slice(0, 3).map(review => ({
            author_name: review.authorAttribution?.displayName || 'Anonymous',
            author_url: review.authorAttribution?.uri || null,
            profile_photo_url: review.authorAttribution?.photoUri || null,
            rating: review.rating || null,
            text: review.text?.text || review.text || '',
            relative_time_description: review.relativePublishTimeDescription || null,
            time: review.publishTime || null,
            language: review.language || 'en',
            original_language: review.original_language || null,
            translated: review.translated || false
          })),
          
          // ✅ Editorial summary
          editorial_summary: place.editorialSummary?.text || null,
          
          // ✅ Price level (0-4, where 0 is free and 4 is very expensive)
          price_level: place.priceLevel || null,
          
          // ✅ Smart categorization for database tagging
          ...categorizeProgramType(place)
        };

        // Validate transformed coordinates
        if (isNaN(transformed.location.lat) || isNaN(transformed.location.lng)) {
          console.error(`❌ Invalid coordinates for: ${transformed.name}`);
          return null;
        }

        return transformed;
      } catch (error) {
        console.error(`❌ Failed to transform place:`, error, place);
        return null;
      }
    })
    .filter(Boolean) // Remove null entries
    .filter(place => isYouthProgramRelevant(place));
};

/**
 * Categorize program based on name, types, and editorial summary
 * This categorization will be saved to database when user favorites/saves
 */
const categorizeProgramType = (place) => {
  const name = (place.displayName?.text || '').toLowerCase();
  const types = place.types || [];
  const editorialSummary = (place.editorialSummary?.text || '').toLowerCase();
  const searchText = `${name} ${editorialSummary}`.toLowerCase();
  
  // Detect if it's a youth-focused program
  const youthKeywords = [
    'children', 'kids', 'youth', 'junior', 'teen', 'toddler',
    'camp', 'summer camp', 'day camp', 'after school'
  ];
  
  const isYouthProgram = youthKeywords.some(keyword => 
    searchText.includes(keyword)
  );
  
  // Category detection with priority order
  const categories = [];
  
  // 1. Summer Camps (highest priority for our app)
  if (searchText.includes('camp') || 
      searchText.includes('summer camp') || 
      searchText.includes('day camp') ||
      searchText.includes('overnight camp') ||
      searchText.includes('sleepaway camp') ||
      types.includes('campground')) {
    categories.push('Summer Camp');
  }
  
  // 2. Sports & Recreation
  if (searchText.match(/sport|athletic|swim|soccer|basketball|baseball|tennis|football|volleyball|martial arts|gymnastics/) ||
      types.some(t => ['gym', 'sports_club', 'sports_complex', 'stadium', 'swimming_pool'].includes(t))) {
    categories.push('Sports & Recreation');
  }
  
  // 3. STEM & Education
  if (searchText.match(/stem|science|coding|robot|tech|computer|engineering|math/) ||
      types.some(t => ['school', 'university', 'library'].includes(t))) {
    categories.push('STEM & Education');
  }
  
  // 4. Arts & Culture
  if (searchText.match(/art|music|dance|theater|drama|painting|drawing|craft|pottery/) ||
      types.some(t => ['art_gallery', 'museum', 'performing_arts_theater'].includes(t))) {
    categories.push('Arts & Culture');
  }
  
  // 5. Outdoor & Adventure
  if (searchText.match(/outdoor|nature|hiking|adventure|climbing|kayak|canoe/) ||
      types.some(t => ['park', 'national_park', 'campground'].includes(t))) {
    categories.push('Outdoor & Adventure');
  }
  
  // 6. Community Programs
  if (searchText.match(/ymca|boys girls club|community center|recreation center/) ||
      types.includes('community_center')) {
    categories.push('Community Programs');
  }
  
  // 7. Enrichment
  if (searchText.match(/enrichment|tutoring|learning|education|language/) ||
      types.includes('child_care_agency')) {
    categories.push('Enrichment');
  }
  
  return {
    primary_category: categories[0] || 'General Youth Program',
    all_categories: categories,
    is_youth_program: isYouthProgram,
    confidence: categories.length > 0 ? 'high' : 'medium',
    keywords_matched: youthKeywords.filter(k => searchText.includes(k))
  };
};

/**
 * Smart categorization for youth programs using types, name, and editorial summary
 */
const categorizePlaceByType = (place) => {
  const types = place.types || [];
  const name = (place.displayName?.text || '').toLowerCase();
  const summary = (place.editorialSummary?.text || '').toLowerCase();
  const allText = `${name} ${summary}`;
  
  // Camp-specific keywords (highest priority)
  const campKeywords = ['camp', 'summer camp', 'day camp', 'overnight camp', 'youth camp'];
  if (campKeywords.some(keyword => allText.includes(keyword))) {
    return { category: 'camp', icon: '🏕️', priority: 1 };
  }
  
  // Educational programs
  if (types.includes('school') || types.includes('university') || types.includes('primary_school') || types.includes('secondary_school')) {
    return { category: 'education', icon: '🏫', priority: 2 };
  }
  
  // Sports and recreation
  if (types.includes('gym') || types.includes('sports_complex') || types.includes('sports_club') || 
      allText.includes('sports') || allText.includes('athletic') || allText.includes('fitness')) {
    return { category: 'sports', icon: '⚽', priority: 3 };
  }
  
  // Arts and culture
  if (types.includes('museum') || types.includes('art_gallery') || types.includes('performing_arts_theater') ||
      allText.includes('art') || allText.includes('music') || allText.includes('dance') || allText.includes('theater')) {
    return { category: 'arts', icon: '🎨', priority: 4 };
  }
  
  // Outdoor and adventure
  if (types.includes('park') || types.includes('campground') || types.includes('national_park') ||
      allText.includes('outdoor') || allText.includes('hiking') || allText.includes('nature')) {
    return { category: 'outdoor', icon: '🌲', priority: 5 };
  }
  
  // STEM and science
  if (types.includes('aquarium') || types.includes('zoo') || types.includes('planetarium') ||
      allText.includes('science') || allText.includes('technology') || allText.includes('engineering') || allText.includes('math')) {
    return { category: 'stem', icon: '🔬', priority: 6 };
  }
  
  // Community centers and childcare
  if (types.includes('community_center') || types.includes('child_care_agency') ||
      allText.includes('community') || allText.includes('childcare') || allText.includes('daycare')) {
    return { category: 'community', icon: '🏢', priority: 7 };
  }
  
  // Entertainment
  if (types.includes('amusement_park') || types.includes('bowling_alley') || types.includes('movie_theater') ||
      allText.includes('entertainment') || allText.includes('fun') || allText.includes('play')) {
    return { category: 'entertainment', icon: '🎪', priority: 8 };
  }
  
  // Default fallback
  return { category: 'business', icon: '🏢', priority: 9 };
};

/**
 * Enhanced filter for youth program relevance using multiple data sources
 */
const isYouthProgramRelevant = (place) => {
  const youthKeywords = [
    'camp', 'summer camp', 'day camp', 'overnight camp', 'youth camp',
    'youth', 'children', 'kids', 'teen', 'junior',
    'ymca', 'boys girls club', 'boys and girls club',
    'recreation', 'after school', 'enrichment', 'program',
    'childcare', 'daycare', 'preschool'
  ];
  
  const name = (place.name || '').toLowerCase();
  const address = (place.formatted_address || '').toLowerCase();
  const summary = (place.editorial_summary || '').toLowerCase();
  const allText = `${name} ${address} ${summary}`;
  
  // Check for youth-related keywords
  const hasYouthKeywords = youthKeywords.some(keyword => allText.includes(keyword));
  
  // Check for youth-related types
  const youthTypes = ['school', 'primary_school', 'secondary_school', 'preschool', 'child_care_agency', 'community_center'];
  const hasYouthTypes = (place.types || []).some(type => youthTypes.includes(type));
  
  // Check for high quality: good rating AND sufficient reviews
  const hasHighQuality = place.rating && place.rating >= 4.0 && 
                         place.user_ratings_total && place.user_ratings_total >= 10;
  
  // Include ONLY if youth-related AND high quality
  return (hasYouthKeywords || hasYouthTypes) && hasHighQuality;
};

/**
 * Get the hero photo for a place (cost-optimized)
 */
export const getBestPhoto = (place) => {
  if (!place.photos || place.photos.length === 0) return null;
  
  // Return the first (and only) hero photo
  return place.photos[0];
};

/**
 * Get photo URL from Google Places photo reference
 */
export const getPhotoUrl = (photo, maxWidth = 400) => {
  if (!photo || !photo.name) {
    console.warn('⚠️ placesAPI getPhotoUrl: Missing photo or photo.name');
    return null;
  }
  
  console.log('📸 getPhotoUrl called with photo:', photo);
  
  // Use the pre-constructed photoUrl or construct one
  if (photo.photoUrl) {
    // Validate pre-constructed URL
    if (/^(ChIJ|GhIJ|EhIJ)/.test(photo.photoUrl)) {
      console.error('❌ placesAPI getPhotoUrl: Pre-constructed photoUrl appears to be a Place ID!', photo.photoUrl);
      return null;
    }
    console.log('📸 Using pre-constructed photoUrl:', photo.photoUrl);
    return photo.photoUrl;
  }
  
  // Validate photo.name format - must start with "places/" and contain "/photos/"
  if (!photo.name || typeof photo.name !== 'string' || photo.name.trim() === '') {
    console.warn('⚠️ placesAPI getPhotoUrl: Missing or invalid photo.name:', photo.name);
    return null;
  }
  
  if (!photo.name.startsWith('places/') || !photo.name.includes('/photos/')) {
    console.warn('⚠️ placesAPI getPhotoUrl: Invalid photo.name format (expected "places/.../photos/..."):', photo.name);
    return null;
  }
  
  // Validate API key
  const apiKey = getApiKey();
  if (!apiKey || apiKey.trim() === '') {
    console.warn('⚠️ placesAPI getPhotoUrl: Missing or empty API key');
    return null;
  }
  
  // Fallback: construct the photo URL using NEW Places API (v1) format
  const constructedUrl = `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=${maxWidth}&key=${apiKey}`;
  
  // Ensure constructed URL is never a Place ID
  if (/^(ChIJ|GhIJ|EhIJ)/.test(constructedUrl)) {
    console.error('❌ placesAPI getPhotoUrl: Constructed URL appears to be a Place ID! This should never happen:', constructedUrl);
    return null;
  }
  
  console.log('📸 Constructed photo URL:', constructedUrl);
  return constructedUrl;
};

/**
 * Get formatted price level
 */
export const getPriceLevel = (priceLevel) => {
  if (priceLevel === null || priceLevel === undefined) return null;
  
  const priceLevels = {
    0: { text: 'Free', symbol: '🆓', color: 'green' },
    1: { text: 'Inexpensive', symbol: '$', color: 'green' },
    2: { text: 'Moderate', symbol: '$$', color: 'yellow' },
    3: { text: 'Expensive', symbol: '$$$', color: 'orange' },
    4: { text: 'Very Expensive', symbol: '$$$$', color: 'red' }
  };
  
  return priceLevels[priceLevel] || null;
};

/**
 * Get top reviews for a place (already limited to 3 in transform)
 */
export const getTopReviews = (place, limit = 3) => {
  if (!place.reviews || place.reviews.length === 0) return [];
  
  // Reviews are already sorted and limited in transformPlaces
  return place.reviews.slice(0, limit);
};

/**
 * Fetch detailed information about a specific place from Google Places API
 * 
 * @param {string} placeId - Google Place ID
 * @param {Object} options - Fetch options
 * @param {string[]} options.fields - Specific fields to fetch (optional)
 * @param {string} options.fieldSet - Use predefined field set: 'minimal', 'search', 'essential', 'enhanced', 'maximum' (optional)
 * @returns {Promise<Object>} Place details
 * 
 * @example
 * // Using predefined field set (recommended)
 * const details = await fetchPlaceDetails(placeId, { fieldSet: 'maximum' });
 * 
 * @example
 * // Using custom fields
 * const details = await fetchPlaceDetails(placeId, { 
 *   fields: ['places.displayName', 'places.websiteUri', 'places.nationalPhoneNumber'] 
 * });
 */
export async function fetchPlaceDetails(placeId, options = {}) {
  const apiKey = getApiKey();

  if (!apiKey) {
    const checkedVars = [
      process.env.VITE_GOOGLE_MAPS_API_KEY ? 'VITE_GOOGLE_MAPS_API_KEY (found)' : 'VITE_GOOGLE_MAPS_API_KEY (not found)',
      process.env.GOOGLE_PLACES_API_KEY ? 'GOOGLE_PLACES_API_KEY (found)' : 'GOOGLE_PLACES_API_KEY (not found)',
      process.env.GOOGLE_MAPS_API_KEY ? 'GOOGLE_MAPS_API_KEY (found)' : 'GOOGLE_MAPS_API_KEY (not found)'
    ].join(', ');
    throw new Error(`Google Places API key is not configured. Checked: ${checkedVars}. Add one of these to your .env.local file.`);
  }
  
  // Debug: Log which key source is being used (first 10 chars only for security)
  // Match the priority order from getApiKey()
  if (process.env.NODE_ENV !== 'production' && typeof process !== 'undefined') {
    const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;
    const isVite = typeof import.meta !== 'undefined' && import.meta.env;
    
    let keySource = 'unknown';
    if (isNode && !isVite) {
      // Node.js priority: GOOGLE_MAPS_API_KEY first
      keySource = process.env.GOOGLE_MAPS_API_KEY ? 'GOOGLE_MAPS_API_KEY'
        : process.env.GOOGLE_PLACES_API_KEY ? 'GOOGLE_PLACES_API_KEY'
        : process.env.VITE_GOOGLE_MAPS_API_KEY ? 'VITE_GOOGLE_MAPS_API_KEY'
        : 'unknown';
    } else if (isVite && import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
      keySource = 'VITE_GOOGLE_MAPS_API_KEY';
    } else {
      keySource = process.env.GOOGLE_MAPS_API_KEY ? 'GOOGLE_MAPS_API_KEY'
        : process.env.GOOGLE_PLACES_API_KEY ? 'GOOGLE_PLACES_API_KEY'
        : process.env.VITE_GOOGLE_MAPS_API_KEY ? 'VITE_GOOGLE_MAPS_API_KEY'
        : 'unknown';
    }
    console.log(`[Places API] Using key from: ${keySource} (${apiKey.substring(0, 10)}...)`);
  }

  if (!placeId) {
    throw new Error('Place ID is required');
  }

  // Determine which fields to fetch
  let fields;
  if (options.fields) {
    // Custom field list provided
    fields = options.fields;
  } else if (options.fieldSet) {
    // Use predefined field set
    fields = getFieldSetForUseCase(options.fieldSet);
  } else {
    // Default to ESSENTIAL (backward compatible)
    console.warn('No fields or fieldSet specified, defaulting to ESSENTIAL');
    fields = FIELD_SETS.ESSENTIAL;
  }

  // For GET endpoint, field mask should use field names without 'places.' prefix
  // The 'places.' prefix is in the response structure, not the request field mask
  const fieldsForMask = fields.map(field => {
    // Remove 'places.' prefix if present for GET endpoint field mask
    return field.startsWith('places.') ? field.substring(7) : field;
  });
  const fieldMask = formatFieldMask(fieldsForMask);
  const estimatedCost = estimateFieldCost(fields);

  // Log for cost tracking in development
  const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
  if (isDev || process.env.NODE_ENV !== 'production') {
    console.log(`[Places API] Fetching ${placeId}`, {
      fieldCount: fields.length,
      estimatedCost: `$${estimatedCost.toFixed(4)}`,
      fieldSet: options.fieldSet || 'custom'
    });
  }

  // Clean place ID (remove any prefixes)
  const cleanPlaceId = placeId.replace('places/', '').replace(/^places:\/\//, '');

  // Use Places API v1 format with X-Goog-FieldMask header
  const url = `${BASE_URL}/${cleanPlaceId}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': fieldMask
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Places API] Error:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        placeId: cleanPlaceId
      });

      // Handle specific error cases
      if (response.status === 404) {
        throw new Error(`Place not found: ${placeId}`);
      } else if (response.status === 400) {
        throw new Error(`Invalid request for place: ${placeId}`);
      } else if (response.status === 429) {
        throw new Error('Google Places API quota exceeded');
      } else if (response.status === 403) {
        throw new Error('Google Places API request denied - check API key');
      }

      throw new Error(`Places API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('[Places API] Fetch failed:', error);
    throw error;
  }
}

/**
 * Fetch minimal Place Details (website, international phone) for enrichment
 * Cost-optimized: requests only needed fields using Places API v1
 * @deprecated Use fetchPlaceDetails with fieldSet option instead
 */
export async function fetchPlaceDetailsBasic(placeId) {
  console.warn('fetchPlaceDetailsBasic is deprecated, use fetchPlaceDetails with fieldSet option');
  try {
    const details = await fetchPlaceDetails(placeId, { 
      fields: ['places.websiteUri', 'places.internationalPhoneNumber'] 
    });
    return {
      website: details?.websiteUri || null,
      phone: details?.internationalPhoneNumber || null,
    };
  } catch {
    return null;
  }
}

/**
 * Fetch complete place details (all fields)
 * Convenience wrapper for maximum field set
 * 
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object>} Complete place details
 */
export async function fetchPlaceDetailsComplete(placeId) {
  return fetchPlaceDetails(placeId, { fieldSet: 'maximum' });
}

/**
 * Transform database camp format to app format
 */
export const transformDatabaseCamp = (dbCamp) => {
  console.log('🔄 transformDatabaseCamp called for:', dbCamp?.name || 'Unknown');
  console.log('📊 Raw dbCamp object keys:', dbCamp ? Object.keys(dbCamp) : 'null');
  console.log('📊 dbCamp data:', {
    hasPhotos: !!dbCamp.photos,
    photosType: typeof dbCamp.photos,
    photosValue: dbCamp.photos ? (typeof dbCamp.photos === 'string' ? dbCamp.photos.substring(0, 100) + '...' : 'Array/Object') : 'null/undefined',
    user_ratings_total: dbCamp.user_ratings_total,
    review_count: dbCamp.review_count,
    reviews: dbCamp.reviews ? (Array.isArray(dbCamp.reviews) ? `Array(${dbCamp.reviews.length})` : typeof dbCamp.reviews) : 'null/undefined',
    rating: dbCamp.rating,
    primary_category: dbCamp.primary_category,
    category: dbCamp.category,
    name: dbCamp.name,
    camp_type: dbCamp.camp_type
  });
  
  // Parse photos from JSON string if needed
  let parsedPhotos = [];
  let imageUrl = null;
  
  if (dbCamp.photos) {
    try {
      // Database stores photos as JSON string
      parsedPhotos = typeof dbCamp.photos === 'string' 
        ? JSON.parse(dbCamp.photos) 
        : dbCamp.photos;
      
      // Extract image_url from first photo
      if (Array.isArray(parsedPhotos) && parsedPhotos.length > 0) {
        const firstPhoto = parsedPhotos[0];
        
        // Check for pre-constructed URL first
        imageUrl = firstPhoto.photoUrl || firstPhoto.image_url || firstPhoto.url || null;
        
        // If no pre-constructed URL, construct from photo.name (database stores raw photo objects)
        if (!imageUrl && firstPhoto.name && typeof firstPhoto.name === 'string') {
          // Validate photo.name format
          if (firstPhoto.name.startsWith('places/')) {
            const apiKey = getApiKey();
            if (apiKey && apiKey.trim() !== '') {
              imageUrl = `https://places.googleapis.com/v1/${firstPhoto.name}/media?maxWidthPx=400&key=${apiKey}`;
              console.log('✅ transformDatabaseCamp: Constructed image_url from photo.name:', imageUrl);
            } else {
              console.warn('⚠️ transformDatabaseCamp: Missing API key, cannot construct photo URL from photo.name');
            }
          } else {
            console.warn('⚠️ transformDatabaseCamp: Invalid photo.name format (expected "places/..."):', firstPhoto.name);
          }
        }
        
        console.log('✅ Found photo in dbCamp:', {
          photoUrl: firstPhoto.photoUrl,
          image_url: firstPhoto.image_url,
          url: firstPhoto.url,
          photoName: firstPhoto.name,
          finalImageUrl: imageUrl,
          constructedFromName: !firstPhoto.photoUrl && !firstPhoto.image_url && !firstPhoto.url && imageUrl !== null
        });
      }
    } catch (error) {
      console.warn('Failed to parse photos for:', dbCamp.name, error);
      parsedPhotos = [];
    }
  } else {
    console.log('❌ No photos field in dbCamp');
  }
  
  // Get review count - check multiple possible field names
  const reviewCount = dbCamp.user_ratings_total || dbCamp.review_count || dbCamp.reviews?.length || 0;
  
  const transformed = {
    id: dbCamp.id, // ✅ CRITICAL: Include database UUID so database camps can be identified correctly
    // Preserve place_id from DB so session lookup works (parent_place_id in camp_audit_data matches parent's place_id).
    // Enrichment still uses isValidGooglePlaceId(place_id) before calling Google; Get Directions uses address/location.
    place_id: dbCamp.place_id != null && dbCamp.place_id !== '' ? dbCamp.place_id : null,
    name: dbCamp.name,
    formatted_address: dbCamp.formatted_address || dbCamp.location || dbCamp.address,
    location: {
      lat: Number(dbCamp.coordinates_lat || dbCamp.latitude || dbCamp.lat),
      lng: Number(dbCamp.coordinates_lng || dbCamp.longitude || dbCamp.lng)
    },
    geometry: {
      location: {
        lat: () => Number(dbCamp.coordinates_lat || dbCamp.latitude || dbCamp.lat),
        lng: () => Number(dbCamp.coordinates_lng || dbCamp.longitude || dbCamp.lng)
      }
    },
    rating: dbCamp.rating || null,
    user_ratings_total: reviewCount,
    reviewCount: reviewCount, // Add reviewCount alias for component compatibility
    review_count: reviewCount, // Also map review_count for database compatibility
    types: dbCamp.google_types || dbCamp.types || [],
    business_status: dbCamp.business_status || 'OPERATIONAL',
    
    // ✅ Parse photos and ensure each photo has photoUrl for UnifiedProgramCard
    photos: parsedPhotos.map(photo => {
      // If photo already has photoUrl, use it
      if (photo.photoUrl) {
        return photo;
      }
      
      // Otherwise, construct photoUrl from photo.name if available
      if (photo.name && typeof photo.name === 'string' && photo.name.startsWith('places/')) {
        const apiKey = getApiKey();
        if (apiKey && apiKey.trim() !== '') {
          return {
            ...photo,
            photoUrl: `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=400&key=${apiKey}`
          };
        }
      }
      
      // Return photo as-is if we can't construct URL
      return photo;
    }),
    photo_count: dbCamp.photo_count || parsedPhotos.length,
    image_url: imageUrl,  // ✅ This is what UI components need
    
    reviews: dbCamp.reviews || [],
    editorial_summary: dbCamp.editorial_summary || dbCamp.description,
    // Contact/website: prefer contribution columns (contact_phone, website_url) then legacy (phone, website)
    phone: dbCamp.contact_phone || dbCamp.phone || null,
    website: dbCamp.website_url || dbCamp.website || null,
    contact_phone: dbCamp.contact_phone || dbCamp.phone || null,
    contact_email: dbCamp.contact_email || dbCamp.email || null,
    website_url: dbCamp.website_url || dbCamp.website || null,
    price_level: dbCamp.price_level,
    
    // ✅ Community contribution fields (so "Community Verified" and contributed data show in UI)
    has_community_enhancements: dbCamp.has_community_enhancements ?? false,
    community_contribution_count: dbCamp.community_contribution_count ?? 0,
    user_contributed: dbCamp.user_contributed ?? false,
    data_confidence_score: dbCamp.data_confidence_score ?? null,
    last_community_update: dbCamp.last_community_update ?? null,
    
    // ✅ Extract category for UI display - check multiple possible field names
    category: dbCamp.primary_category || dbCamp.category || dbCamp.camp_type || 'Program',
    primary_category: dbCamp.primary_category || dbCamp.category || dbCamp.camp_type || 'Program',
    
    // ✅ Preserve camp_type and activities for perfect match logic
    camp_type: dbCamp.camp_type || null,
    activities: dbCamp.activities || null,
    all_categories: dbCamp.all_categories || [],
    
    // Keep the full program_category object for filtering
    program_category: {
      primary_category: dbCamp.primary_category || dbCamp.category,
      all_categories: dbCamp.all_categories || [],
      confidence: dbCamp.category_confidence,
      keywords_matched: dbCamp.keywords_matched || []
    },
    // Include engagement metrics for sorting
    _engagement: {
      view_count: dbCamp.view_count || 0,
      save_count: dbCamp.save_count || 0,
      popularity_score: dbCamp.popularity_score || 0
    }
  };
  
  console.log('✅ transformDatabaseCamp result:', {
    name: transformed.name,
    reviewCount: transformed.reviewCount,
    image_url: transformed.image_url,
    photosLength: transformed.photos.length,
    category: transformed.category,
    primary_category: transformed.primary_category,
    camp_type: transformed.camp_type,
    activities: transformed.activities,
    all_categories: transformed.all_categories?.length || 0,
    has_community_enhancements: transformed.has_community_enhancements,
    community_contribution_count: transformed.community_contribution_count
  });
  
  return transformed;
};

/**
 * Enrich database camp with Google Places data if missing critical fields
 * Only fetches if place_id exists and data is missing
 */
export const enrichDatabaseCampWithGoogleData = async (dbCamp) => {
  // Only enrich if we have a place_id and are missing critical data
  const placeId = dbCamp.place_id;
  if (!placeId || !isValidGooglePlaceId(placeId)) {
    console.warn('[Enrichment] Skipping camp - missing or invalid place_id', {
      campId: dbCamp.id,
      campName: dbCamp.name,
      providedPlaceId: dbCamp.place_id,
      reason: !placeId ? 'missing place_id' : 'invalid place_id format'
    });
    return null; // Return null to indicate no enrichment needed
  }

  // Check if we're missing critical data - REDUCED: Only check photos (most important for UI)
  // Removed checks for reviews, rating, website, phone to reduce API calls
  const hasPhotos = dbCamp.photos && (
    (typeof dbCamp.photos === 'string' && dbCamp.photos.trim() !== '[]' && dbCamp.photos !== 'null' && dbCamp.photos.trim() !== '') ||
    (Array.isArray(dbCamp.photos) && dbCamp.photos.length > 0)
  );
  const hasImageUrl = dbCamp.image_url || dbCamp.photo_url;

  // If we have photos, skip enrichment (reduced from checking all fields)
  if (hasPhotos && hasImageUrl) {
    console.log('✅ Skipping enrichment: Camp already has photos', {
      name: dbCamp.name || dbCamp.display_name,
      hasPhotos,
      hasImageUrl
    });
    return null; // Return null to indicate no enrichment needed
  }

  console.log('🔄 Enriching camp data from Google Places API (photos only):', {
    name: dbCamp.name,
    placeId,
    missing: {
      photos: !hasPhotos,
      imageUrl: !hasImageUrl
    }
  });

  try {
    const enrichmentApiKey = getApiKey();
    if (!enrichmentApiKey) {
      console.warn('⚠️ No Google Maps API key, skipping enrichment');
      return null;
    }

    // Use deduplication to prevent multiple simultaneous enrichment requests for same place
    const cleanPlaceId = placeId.replace('places/', '').replace(/^places:\/\//, '');
    
    // Validate place ID format (should be a UUID-like string)
    if (!cleanPlaceId || cleanPlaceId.length < 10) {
      console.warn('⚠️ Invalid place ID format for enrichment:', cleanPlaceId);
      return null;
    }
    
    const cacheKey = `enrich:${cleanPlaceId}`;
    const placeData = await deduplicateRequest(cacheKey, async () => {
      // Fetch place details using NEW Places API
      // REDUCED: Only fetch photos field to minimize API cost
      // Note: Place ID in new API format is just the ID, not the full resource name
      // BASE_URL is already 'https://places.googleapis.com/v1/places', so we just append the ID
      const response = await fetch(`${BASE_URL}/${cleanPlaceId}`, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': enrichmentApiKey,
          'X-Goog-FieldMask': 'id,displayName,photos' // Reduced from 'id,displayName,rating,userRatingCount,photos,types'
        }
      });

      if (!response.ok) {
        // Read error response for better debugging
        let errorMessage = `Status ${response.status}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `${errorMessage}: ${errorText}`;
          }
        } catch (e) {
          // Ignore if we can't read error body
        }
        console.warn('⚠️ Failed to fetch place details for enrichment:', errorMessage, 'Place ID:', cleanPlaceId);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    });
    
    // Merge missing data into dbCamp object
    const enriched = { ...dbCamp };

    // REDUCED: Only enrich photos (removed review count, category, rating, website, phone enrichment)
    // Add photos if missing
    if (!hasPhotos && placeData.photos && placeData.photos.length > 0) {
      // Transform photos to our format using NEW Places API (v1) format
      const transformedPhotos = placeData.photos.map(photo => {
        const photoReference = photo.name?.split('/').pop() || photo.photoReference;
        
        // Validate photo.name format
        let photoUrl = null;
        if (photo.name && typeof photo.name === 'string') {
          if (!photo.name.startsWith('places/')) {
            console.warn('⚠️ placesAPI enrichPlaceDetails: Invalid photo.name format:', {
              photoName: photo.name,
              placeId: cleanPlaceId
            });
          } else if (enrichmentApiKey && enrichmentApiKey.trim() !== '') {
            photoUrl = `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=800&key=${enrichmentApiKey}`;
            // Ensure photoUrl is never a Place ID
            if (/^(ChIJ|GhIJ|EhIJ)/.test(photoUrl)) {
              console.error('❌ placesAPI enrichPlaceDetails: Constructed URL appears to be a Place ID!', photoUrl);
              photoUrl = null;
            }
          } else {
            console.warn('⚠️ placesAPI enrichPlaceDetails: Missing API key for photo URL construction');
          }
        }
        
        return {
          photo_reference: photoReference,
          photoUrl: photoUrl,
          width: photo.widthPx,
          height: photo.heightPx
        };
      }).filter(p => p.photoUrl); // Filter out invalid photos

      if (transformedPhotos.length > 0) {
        enriched.photos = typeof enriched.photos === 'string' 
          ? JSON.stringify(transformedPhotos)
          : transformedPhotos;
        console.log('✅ Enriched photos:', transformedPhotos.length);
      }
    }

    // REDUCED: Removed category, rating, website, phone enrichment to minimize API calls
    // Only photos are enriched now

    // Save enriched data to database immediately
    if (enriched && enriched !== dbCamp) {
      try {
        const { saveCampToDatabase } = await import('@/services/campDatabaseService');
        console.log('💾 Saving enriched data to database for:', enriched.name || enriched.display_name);
        
        // Transform enriched data to database format if needed
        // The enriched object should already be in the right format since it's based on dbCamp
        await saveCampToDatabase(enriched);
        console.log('✅ Enriched data saved to database');
      } catch (saveError) {
        console.error('⚠️ Failed to save enriched data (non-fatal):', saveError);
        // Don't throw - saving failure shouldn't break the flow
        // The enriched data is still returned for immediate use
      }
    }
    
    return enriched;
  } catch (error) {
    console.error('❌ Error enriching camp data:', error);
    return null; // Return null on error to prevent breaking the flow
  }
};

/**
 * Background refresh to keep cache fresh
 */
const backgroundRefreshCamps = async (location, radiusMiles) => {
  // Run in background, don't await
  setTimeout(async () => {
    try {
      const results = await searchAllYouthPrograms(location, radiusMiles, { forceRefresh: true });
      console.log(`🔄 Background refresh completed: ${results.length} camps updated`);
    } catch (error) {
      console.error('Background refresh failed:', error);
    }
  }, 5000); // Wait 5 seconds before background refresh
};

